/* ============================================================================
   FRONT LINE DEV - DO NOT EDIT
   This file powers Infinit's custom Quiz and Custom Formula functionality and
   is maintained exclusively by Front Line Dev. Editing it may break the
   formula builder, quiz, saved formulas, or related features. Please contact
   Front Line Dev for any changes.
   ============================================================================ */

/**
 * Saved Formulas / My Formulas Page
 *
 * Features:
 *  - Lists customer's saved formulas with columns: Name, Type, Flavor, Nutrition Info, Updated, Actions
 *  - Actions: Order, Copy, Edit, Delete
 *  - Order: Opens mini PDP modal with packaging, subscription, and pricing options
 *  - Copy: Duplicates the formula
 *  - Edit: Redirects to the Custom Hydration/Protein page with formula config pre-loaded
 *  - Delete: Soft-deletes the formula (sets status to hidden)
 *  - Search and pagination
 */
(() => {
  const root = document.getElementById("saved-formulas-app");
  if (!root) return;

  const base = root.dataset.proxyBase || "/apps/formulas";

  let state = {
    page: 1,
    pageSize: 10,
    query: "",
    sortBy: "updated_at",
    sortDir: "desc",
    items: [],
    total: 0,
    loading: false,
    error: "",
  };

  // ─── Helpers ───

  function qs(obj) {
    const p = new URLSearchParams();
    Object.entries(obj).forEach(([k, v]) => {
      if (v !== undefined && v !== null) p.set(k, String(v));
    });
    return p.toString();
  }

  async function api(method, route, body) {
    const opts = { method, headers: { Accept: "application/json" } };
    let url = base + "/" + route;
    if (body && method !== "GET") {
      opts.headers["Content-Type"] = "application/json";
      opts.body = JSON.stringify(body);
    }
    if (body && method === "GET") {
      url += "?" + qs(body);
    }
    const r = await fetch(url, opts);
    const data = await r.json().catch(() => ({}));
    if (!r.ok || data.ok !== true) {
      throw new Error(data.message || `Request failed (${r.status})`);
    }
    return data;
  }

  function el(tag, attrs, children) {
    const n = document.createElement(tag);
    if (attrs) {
      Object.entries(attrs).forEach(([k, v]) => {
        if (k === "class") n.className = v;
        else if (k === "html") n.innerHTML = v;
        else if (k.startsWith("on") && typeof v === "function")
          n.addEventListener(k.slice(2), v);
        else if (k === "style" && typeof v === "object")
          Object.assign(n.style, v);
        else n.setAttribute(k, v);
      });
    }
    if (children) {
      (Array.isArray(children) ? children : [children]).forEach((c) => {
        if (c == null) return;
        n.appendChild(typeof c === "string" ? document.createTextNode(c) : c);
      });
    }
    return n;
  }

  function fmtDate(str) {
    if (!str) return "";
    const d = new Date(str.replace(" ", "T") + (str.includes("Z") ? "" : "Z"));
    if (isNaN(d)) return str;
    return d.toLocaleDateString("en-US", {
      month: "numeric",
      day: "numeric",
      year: "numeric",
    }) + ", " + d.toLocaleTimeString("en-US", {
      hour: "numeric",
      minute: "2-digit",
    });
  }

  function getFormulaType(item) {
    const p = item.payload || {};
    const ft = (p.formula_type || "").toLowerCase();
    if (ft === "config-fructose") return "Premium Fuel";
    if (ft === "protein") return "Protein";
    if (ft === "hydration") return "Fuel";
    // Fallback for older records saved without formula_type.
    // Hydration formulas always have sliders.electrolytes; protein formulas never do.
    const sliders = p.sliders || {};
    if ("electrolytes" in sliders) return "Fuel";
    // Protein formulas use calorie_carbs slider
    if ("calorie_carbs" in sliders) return "Protein";
    return "Fuel";
  }

  function getFormulaTypeClass(type) {
    return type === "Protein" ? "sf-type-badge--protein" : "sf-type-badge--fuel";
  }

  // ─── Product Data Cache ───

  const productCache = {};
  const PRODUCT_HANDLES = {
    hydration: "custom-hydration-formula",
    protein: "custom-protein-formula",
    "config-fructose": "premium-fructose-fuel",
  };

  // :PREMIUM Fructose Fuel (Configurable Fructose, TW 41436938): fixed options
  // instead of sliders, Flavor and Carb Level are the variant, one bag size.
  function isPremiumFuel(item) {
    return getFormulaType(item) === "Premium Fuel";
  }

  /** The fields config-fructose-nutrition expects, from a saved payload. */
  function premiumFuelFields(p) {
    const f = new URLSearchParams();
    f.set("fructose", p.fructose || "");
    f.set("electrolytes", p.electrolyte_level || "");
    f.set("caffeine", p.caffeine_amount || "");
    f.set("flavor", p.flavor || "");
    return f;
  }

  async function getProductData(formulaType) {
    const key = PRODUCT_HANDLES[formulaType] ? formulaType : "hydration";
    if (productCache[key]) return productCache[key];
    try {
      const handle = PRODUCT_HANDLES[key];
      const resp = await fetch("/products/" + handle + ".js");
      if (!resp.ok) throw new Error("fetch failed");
      const product = await resp.json();
      // Build variant lookup by option value (package size number)
      const variants = {};
      const byId = {};
      (product.variants || []).forEach(function (v) {
        byId[String(v.id)] = v;
        var m = (v.title || v.option1 || "").match(/(\d+)/);
        if (m) variants[m[1]] = v;
      });
      productCache[key] = { product: product, variants: variants, byId: byId };
      return productCache[key];
    } catch (e) {
      return null;
    }
  }

  function formatMoney(cents) {
    if (window.Shopify && typeof Shopify.formatMoney === "function") {
      return Shopify.formatMoney(cents, "${{amount}}");
    }
    return "$" + (cents / 100).toFixed(2);
  }

  // ─── Data Fetching ───

  async function fetchList() {
    const url = `${base}/list?` + qs({
      page: state.page,
      pageSize: state.pageSize,
      query: state.query,
      sortBy: state.sortBy,
      sortDir: state.sortDir,
    });
    const r = await fetch(url, { method: "GET", headers: { Accept: "application/json" } });
    const data = await r.json().catch(() => ({}));
    if (!r.ok || data.ok !== true) {
      throw new Error(data.message || `Request failed (${r.status})`);
    }
    return data;
  }

  // ─── Actions ───

  async function handleOrder(item) {
    openOrderModal(item);
  }

  async function handleCopy(item) {
    if (!confirm(`Copy "${item.formula_name}"?`)) return;
    try {
      const result = await api("POST", "copy", { id: item.id });
      showMsg(`Formula copied as "${result.formula_name}"`, "success");
      reload();
    } catch (e) {
      showMsg("Copy failed: " + e.message, "error");
    }
  }

  function handleEdit(item) {
    const p = item.payload || {};
    if (isPremiumFuel(item)) {
      // Premium Fuel edit mode lives in the Name Config Fructose block. Flavor
      // and Carb Level are chosen by variant; the other options travel in the hash.
      let pfHash = "";
      try {
        pfHash = btoa(unescape(encodeURIComponent(JSON.stringify({
          t: "cf",
          caffeine_amount: p.caffeine_amount || "",
          electrolyte_level: p.electrolyte_level || "",
          boosts: [],
        }))));
      } catch (e) { pfHash = ""; }
      const pfParams = new URLSearchParams({
        edit: "1",
        formula_id: String(item.id),
        formula_name: item.formula_name || "",
      });
      if (p.base_variant_id) pfParams.set("variant", String(p.base_variant_id));
      window.location.href = "/products/" + PRODUCT_HANDLES["config-fructose"] + "?" + pfParams.toString() + (pfHash ? "#" + pfHash : "");
      return;
    }
    const ft = getFormulaType(item);
    const isProtein = ft === "Protein";

    // Build config object for FormulaUrlHash
    const config = {};
    config.type = isProtein ? "p" : "h";

    // Flavor
    if (p.flavor) config.flavor = p.flavor;

    // Flavor intensity
    const fi = p.flavor_intensity || (p.sliders && p.sliders.flavor_intensity);
    if (fi !== undefined && fi !== "") config.flavor_slider = parseInt(fi, 10);

    if (isProtein) {
      // Protein calorie/carbs slider
      const cal = p.sliders ? p.sliders.calories : null;
      if (cal !== undefined && cal !== null && cal !== "") {
        config.calorie_carbs_slider = parseInt(cal, 10);
      }
    } else {
      // Hydration sliders
      const sliders = p.sliders || {};
      if (sliders.calories !== undefined && sliders.calories !== "") config.calories_slider = parseInt(sliders.calories, 10);
      if (sliders.carbohydrates !== undefined && sliders.carbohydrates !== "") config.carbs_slider = parseInt(sliders.carbohydrates, 10);
      if (sliders.electrolytes !== undefined && sliders.electrolytes !== "") config.electrolytes_slider = parseInt(sliders.electrolytes, 10);
      if (sliders.bcaas !== undefined && sliders.bcaas !== "") config.aminoacids_slider = parseInt(sliders.bcaas, 10);
      if (sliders.caffeine !== undefined && sliders.caffeine !== "") config.caffeine_slider = parseInt(sliders.caffeine, 10);
      if (sliders.protein !== undefined && sliders.protein !== "") config.protein_slider = parseInt(sliders.protein, 10);
    }

    // Protein type
    if (p.selected_protein && p.selected_protein !== "None") {
      config.protein_type = p.selected_protein;
    }

    // Boosts
    const boostIds = p.boosts ? (p.boosts.ids || []) : [];
    const boostLabels = p.boosts ? (p.boosts.labels || []) : [];
    if (boostIds.length > 0) {
      config.boosts = boostIds.map(String);
      // Carry labels so the slider can still pre-select boosts whose stored IDs
      // are stale (Magento-migrated formulas) by matching on label — mirrors the
      // nutrition/price self-heal. Without this, those boosts load unchecked.
      if (boostLabels.length > 0) config.boost_labels = boostLabels.map(String);
    }

    // Package size
    if (p.package_size) {
      config.package_size = parseInt(p.package_size, 10);
    }

    // Encode to URL hash using FormulaUrlHash if available
    let hash = "";
    if (window.FormulaUrlHash) {
      hash = window.FormulaUrlHash.encode(config);
    } else {
      // Fallback: manual base64 encoding
      try {
        const KEY_MAP = { type: "t", flavor: "f", flavor_slider: "fi", calorie_carbs_slider: "cc", calories_slider: "cal", carbs_slider: "carb", electrolytes_slider: "el", aminoacids_slider: "aa", caffeine_slider: "caf", protein_slider: "ps", protein_type: "pt", boosts: "b", boost_labels: "bl", package_size: "pk" };
        const compact = {};
        for (const [k, v] of Object.entries(config)) {
          if (v === undefined || v === null || v === "") continue;
          compact[KEY_MAP[k] || k] = v;
        }
        hash = btoa(unescape(encodeURIComponent(JSON.stringify(compact))));
      } catch (e) {
        console.warn("Failed to encode formula hash", e);
      }
    }

    const basePath = isProtein
      ? "/products/custom-protein-formula"
      : "/products/custom-hydration-formula";

    // Pass edit mode params: formula_id for saving back, edit=1 to trigger edit UI
    const editParams = new URLSearchParams({
      edit: "1",
      formula_id: String(item.id),
      formula_name: item.formula_name || "",
    });

    window.location.href = basePath + "?" + editParams.toString() + (hash ? "#" + hash : "");
  }

  async function handleDelete(item) {
    if (!confirm(`Delete "${item.formula_name}"? This cannot be undone.`)) return;
    try {
      await api("POST", "delete?id=" + encodeURIComponent(item.id), {});
      showMsg("Formula deleted.", "success");
      reload();
    } catch (e) {
      showMsg("Delete failed: " + e.message, "error");
    }
  }

  // ─── Nutrition Info ───

  async function fetchNutritionLabel(item) {
    const p = item.payload || {};
    const ft = getFormulaType(item);
    const isProtein = ft === "Protein";
    const sliders = p.sliders || {};

    if (isPremiumFuel(item)) {
      const pf = await fetch(base + "/config-fructose-nutrition", {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: premiumFuelFields(p).toString(),
      });
      const pfData = await pf.json();
      if (!pfData.ok) throw new Error(pfData.message || "Failed to load nutrition data");
      return pfData;
    }

    const endpoint = isProtein ? "protein-nutrition" : "hydration-nutrition";

    // Build form data matching what the nutrition API expects
    const formData = new URLSearchParams();

    if (isProtein) {
      formData.set("protein", p.selected_protein || "");
      formData.set("flavor", p.flavor || "");
      formData.set("flavor_slider", p.flavor_intensity || sliders.flavor_intensity || "50");
      // Use nullish coalescing so a legitimate slider position of 0 (Low Cal)
      // is NOT silently coerced to '50' (Moderate). `||` treats both 0 and ''
      // as falsy, which previously caused Low-Cal saved formulas to render
      // their nutrition label at the Moderate level.
      formData.set("calorie_level", String(sliders.calories ?? "50"));
      formData.set("package_size", p.package_size || "25");
      formData.set("mix_name", p.mix_name || item.formula_name || "");
      // Send boost_labels[] alongside boosts[] so the backend can rewrite stale
      // boost IDs (from Magento-migrated saved formulas) via label lookup — same
      // as fetchFormulaPrice. Without labels, stale-ID boosts (e.g. Beta-Alanine,
      // Creatine, Sodium Citrate) silently drop from the nutrition label.
      const boostIds = p.boosts ? (p.boosts.ids || []) : [];
      const boostLabels = p.boosts ? (p.boosts.labels || []) : [];
      boostIds.forEach((id) => formData.append("boosts[]", String(id)));
      boostLabels.forEach((lbl) => formData.append("boost_labels[]", String(lbl)));
    } else {
      formData.set("flavor", p.flavor || "");
      formData.set("flavor_intensity", p.flavor_intensity || sliders.flavor_intensity || "");
      formData.set("calories", sliders.calories || "");
      formData.set("carbohydrates", sliders.carbohydrates || "");
      formData.set("electrolytes", sliders.electrolytes || "");
      formData.set("protein", sliders.protein || "");
      formData.set("bcaas", sliders.bcaas || "");
      formData.set("caffeine", sliders.caffeine || "");
      formData.set("package_size", p.package_size || "25");
      formData.set("mix_name", p.mix_name || item.formula_name || "");
      if (p.selected_protein && p.selected_protein !== "None") {
        formData.set("selected_protein", p.selected_protein);
      }
      // See note in the protein branch: labels let the backend resolve stale
      // boost IDs so all selected boosts render in the nutrition label.
      const boostIds = p.boosts ? (p.boosts.ids || []) : [];
      const boostLabels = p.boosts ? (p.boosts.labels || []) : [];
      boostIds.forEach((id) => formData.append("boosts[]", String(id)));
      boostLabels.forEach((lbl) => formData.append("boost_labels[]", String(lbl)));
    }

    const resp = await fetch(base + "/" + endpoint, {
      method: "POST",
      headers: { "Content-Type": "application/x-www-form-urlencoded" },
      body: formData.toString(),
    });
    const data = await resp.json();
    if (!data.ok) throw new Error(data.message || "Failed to load nutrition data");
    return data;
  }

  function showNutritionModal(item) {
    const overlay = el("div", { class: "sf-modal-overlay", onclick: (e) => { if (e.target === overlay) overlay.remove(); } });

    const bodyEl = el("div", { class: "sf-modal__body sf-nutrition-label-body", html: '<div style="text-align:center;padding:2rem;color:#999;">Loading nutrition label...</div>' });

    const modal = el("div", { class: "sf-modal sf-modal--nutrition" }, [
      el("div", { class: "sf-modal__header" }, [
        el("h3", { class: "sf-modal__title" }, [item.formula_name + " - Nutrition Facts"]),
        el("button", { class: "sf-modal__close", onclick: () => overlay.remove() }, ["\u00D7"]),
      ]),
      bodyEl,
      el("div", { class: "sf-modal__footer" }, [
        el("button", { class: "sf-btn sf-btn--secondary", onclick: () => overlay.remove() }, ["Close"]),
      ]),
    ]);
    overlay.appendChild(modal);
    document.body.appendChild(overlay);

    // Fetch the real nutrition label
    const mixName = (item.payload && item.payload.mix_name) || item.formula_name || "";
    fetchNutritionLabel(item).then((data) => {
      let html = data.nutrition_html || data.label_html || '';
      if (!html) { bodyEl.innerHTML = '<p style="text-align:center;color:#999;">No nutrition label available.</p>'; return; }
      // Strip the backend-embedded mix name (rendered as <div class="formula-name">...)
      // since the modal header already displays it prominently.
      html = html.replace(/<div class="formula-name">[^<]*<\/div>/i, '');
      bodyEl.innerHTML = html;
    }).catch((err) => {
      bodyEl.innerHTML = '<p style="text-align:center;color:#c62828;">Failed to load nutrition label: ' + err.message + '</p>';
    });
  }

  // ─── Order Modal (Mini PDP) ───

  async function openOrderModal(item) {
    const p = item.payload || {};
    const ft = getFormulaType(item);
    const isProtein = ft === "Protein";

    // Determine available package sizes and their variant IDs
    const hydrationVariants = {
      "5": "50456727224635",
      "12": "50456727257403",
      "24": "50456727290171",
      "25": "50456727322939",
    };
    const proteinVariants = {
      "5": "50727624769851",
      "12": "50727624999227",
      "24": "50727625031995",
      "25": "50727624966459",
    };
    const variantMap = isProtein ? proteinVariants : hydrationVariants;
    const isPF = isPremiumFuel(item);

    // Fetch product data for images and prices
    const productData = await getProductData(isPF ? "config-fructose" : (isProtein ? "protein" : "hydration"));

    // Premium Fuel has no size choice: one bag size for every Carb Level.
    const packageSizes = isPF ? [] : [
      { value: "25", label: "25 Serving Resealable Eco-pack" },
      { value: "12", label: "12 Serving Resealable Eco-pack" },
      { value: "5", label: "5 Serving Resealable Eco-pack" },
      { value: "24", label: "24 Single Serving Packets" },
    ];

    let selectedPkg = p.package_size || "25";
    let selectedPlan = ""; // empty = one-time purchase

    // --- Price + image helpers ---
    function getVariantForSize(size) {
      // Premium Fuel: the saved Flavor and Carb Level variant, whatever the size.
      if (isPF) return (productData && productData.byId[String(p.base_variant_id)]) || null;
      if (productData && productData.variants[size]) return productData.variants[size];
      return null;
    }

    // Price computation via the same backend nutrition endpoint that the
    // slider info-bar uses.  This ensures the Order modal displays exactly
    // the same price the customer would see on the builder page.
    //
    // Results are cached per package-size so changing sizes doesn't re-fetch
    // if we already have the answer. We cache the FULL response (including
    // computed nutrition values) so the cart-add flow can attach real nutrition
    // to the line item instead of slider positions.
    const _priceCache = {};
    const _nutritionCache = {};

    async function fetchFormulaPrice(pkgSize) {
      if (_priceCache[pkgSize] !== undefined) return _priceCache[pkgSize];

      const variant = getVariantForSize(pkgSize);
      if (!variant) return null;

      const sliders = p.sliders || {};
      let fd = new URLSearchParams();
      const endpoint = isPF ? "config-fructose-nutrition" : (isProtein ? "protein-nutrition" : "hydration-nutrition");

      // Positional labels: stored saved-formula payloads from the Magento
      // migration contain stale boost IDs that no longer exist in the current
      // CSV catalogs. The labels array stays positionally aligned with ids,
      // so we send both and let the backend rewrite stale IDs via label lookup.
      const boostIds = p.boosts ? (p.boosts.ids || []) : [];
      const boostLabels = p.boosts ? (p.boosts.labels || []) : [];

      if (isPF) {
        fd = premiumFuelFields(p);
        fd.set("price_cents", String(variant.price));
      } else if (isProtein) {
        fd.set("protein", p.selected_protein || "");
        fd.set("flavor", p.flavor || "");
        fd.set("flavor_slider", p.flavor_intensity || sliders.flavor_intensity || "50");
        // Nullish coalescing — see comment at fetchNutritionLabel above.
        fd.set("calorie_level", String(sliders.calories ?? "50"));
        fd.set("package_size", pkgSize);
        fd.set("price_cents", String(variant.price));
        fd.set("mix_name", p.mix_name || item.formula_name || "");
        boostIds.forEach((id) => fd.append("boosts[]", String(id)));
        boostLabels.forEach((lbl) => fd.append("boost_labels[]", String(lbl)));
      } else {
        fd.set("flavor", p.flavor || "");
        fd.set("flavor_intensity", p.flavor_intensity || sliders.flavor_intensity || "");
        fd.set("calories", sliders.calories || "");
        fd.set("carbohydrates", sliders.carbohydrates || "");
        fd.set("electrolytes", sliders.electrolytes || "");
        fd.set("protein", sliders.protein || "");
        fd.set("bcaas", sliders.bcaas || "");
        fd.set("caffeine", sliders.caffeine || "");
        fd.set("package_size", pkgSize);
        fd.set("price_cents", String(variant.price));
        fd.set("mix_name", p.mix_name || item.formula_name || "");
        if (p.selected_protein && p.selected_protein !== "None") {
          fd.set("selected_protein", p.selected_protein);
        }
        boostIds.forEach((id) => fd.append("boosts[]", String(id)));
        boostLabels.forEach((lbl) => fd.append("boost_labels[]", String(lbl)));
      }

      try {
        const resp = await fetch(base + "/" + endpoint, {
          method: "POST",
          headers: { "Content-Type": "application/x-www-form-urlencoded" },
          body: fd.toString(),
        });
        const data = await resp.json();
        if (data.ok && data.final_price_cents != null) {
          _priceCache[pkgSize] = data.final_price_cents;
          // Cache the full nutrition payload so the cart-add flow can include
          // real computed values (calories/sodium/protein/etc.) instead of
          // sending only the saved slider positions, which the cart would
          // display verbatim and confuse customers.
          _nutritionCache[pkgSize] = data;
          return data.final_price_cents;
        }
      } catch (e) {
        console.error("[saved-formulas] price fetch error:", e);
      }
      return null;
    }

    function getVariantImage(size) {
      const variant = getVariantForSize(size);
      if (variant && variant.featured_image && variant.featured_image.src) {
        return variant.featured_image.src.replace(/(\.\w+)\?/, "$1?width=400&");
      }
      // Fallback to product featured image
      if (productData && productData.product && productData.product.featured_image) {
        return productData.product.featured_image.replace(/(\.\w+)\?/, "$1?width=400&");
      }
      if (isPF) return "";
      return isProtein
        ? "/cdn/shop/files/Custom_Protein_Bag.png?v=1&width=400"
        : "/cdn/shop/files/Custom_Hydration_Bag.png?v=1&width=400";
    }

    async function updatePriceDisplay() {
      if (!priceEl) return;
      priceEl.textContent = "Calculating...";
      const priceCents = await fetchFormulaPrice(selectedPkg);
      if (priceCents !== null) {
        let display = priceCents;
        if (selectedPlan) {
          display = Math.round(priceCents * 0.9);
          priceEl.innerHTML = formatMoney(display) + ' <span class="sf-order__price-note">with subscription</span>';
        } else {
          priceEl.textContent = formatMoney(display);
        }
      } else {
        // Fallback to variant base price if backend call fails
        const v = getVariantForSize(selectedPkg);
        if (v) {
          priceEl.textContent = formatMoney(v.price);
        } else {
          priceEl.textContent = "";
        }
      }
    }

    function updateImage() {
      if (!imgEl) return;
      imgEl.src = getVariantImage(selectedPkg);
    }

    // Placeholder refs (assigned after element creation)
    let priceEl = null;
    let imgEl = null;

    const overlay = el("div", {
      class: "sf-modal-overlay",
      onclick: (e) => { if (e.target === overlay) overlay.remove(); },
    });

    // Package size radios
    const pkgGroup = el("div", { class: "sf-order__section" });
    if (isPF) pkgGroup.style.display = "none";
    pkgGroup.appendChild(el("h4", { class: "sf-order__section-title" }, ["Packaging"]));
    packageSizes.forEach((pkg) => {
      const id = "pkg-" + pkg.value;
      const label = el("label", { class: "sf-order__radio-label", for: id }, [
        el("input", {
          type: "radio",
          name: "order-pkg",
          id: id,
          value: pkg.value,
          ...(pkg.value === selectedPkg ? { checked: "checked" } : {}),
          onchange: () => { selectedPkg = pkg.value; updatePriceDisplay(); updateImage(); },
        }),
        " " + pkg.label,
      ]);
      pkgGroup.appendChild(label);
    });

    // Subscription options
    const subGroup = el("div", { class: "sf-order__section" });
    subGroup.appendChild(el("h4", { class: "sf-order__section-title" }, ["Purchase Options"]));

    const subOptions = [
      { value: "", label: "One-time purchase" },
      { value: "4weeks", label: "Subscribe & Save - Every 4 weeks (10% off)" },
      { value: "6weeks", label: "Subscribe & Save - Every 6 weeks (10% off)" },
      { value: "8weeks", label: "Subscribe & Save - Every 8 weeks (10% off)" },
    ];

    subOptions.forEach((opt) => {
      const id = "sub-" + (opt.value || "onetime");
      const label = el("label", { class: "sf-order__radio-label", for: id }, [
        el("input", {
          type: "radio",
          name: "order-sub",
          id: id,
          value: opt.value,
          ...(opt.value === selectedPlan ? { checked: "checked" } : {}),
          onchange: () => { selectedPlan = opt.value; updatePriceDisplay(); },
        }),
        " " + opt.label,
      ]);
      subGroup.appendChild(label);
    });

    // Nutrition info — opens in a modal popup
    const nutritionToggle = el("button", {
      class: "sf-order__nutrition-toggle",
      onclick: () => showNutritionModal(item),
    }, ["Nutritional Info"]);

    // Resolve selling plan ID from product data for a given interval
    function resolveSellingPlan(interval) {
      if (!interval || !productData || !productData.product) return null;
      const weekMap = { "4weeks": 4, "6weeks": 6, "8weeks": 8 };
      const weeks = weekMap[interval];
      if (!weeks) return null;
      const groups = productData.product.selling_plan_groups || [];
      for (const group of groups) {
        for (const plan of (group.selling_plans || [])) {
          // Match by name containing the week count (e.g. "Every 4 weeks")
          const name = (plan.name || "").toLowerCase();
          if (name.includes(String(weeks) + " week")) return plan.id;
          // Also check delivery_policy or recurring_policy interval count
          const opts = plan.options || [];
          for (const opt of opts) {
            if (String(opt.value).includes(String(weeks))) return plan.id;
          }
        }
      }
      return null;
    }

    const orderBtn = el("button", {
      class: "sf-btn sf-btn--primary sf-btn--lg",
      onclick: async () => {
        orderBtn.disabled = true;
        orderBtn.textContent = "Adding to cart...";
        try {
          const baseVariantId = isPF
            ? String(p.base_variant_id || "")
            : (variantMap[selectedPkg] || variantMap["25"]);

          // Ensure we have current computed nutrition for the selected package
          // size before submitting — saved payload sliders are positions (0-100),
          // not real calorie/sodium/protein values, so without this the cart
          // would display the slider positions verbatim (e.g. "Calories: 63"
          // instead of "Calories: 287"). fetchFormulaPrice populates the
          // _nutritionCache as a side effect.
          if (!_nutritionCache[selectedPkg]) {
            await fetchFormulaPrice(selectedPkg);
          }
          const nut = _nutritionCache[selectedPkg] || null;

          const payload = Object.assign({}, p, {
            base_variant_id: baseVariantId,
            package_size: selectedPkg,
            quantity: 1,
            formula_id: item.id,
          });

          // Resolve selling plan ID before sending to the backend.
          // The backend reads $data['selling_plan'] (numeric ID), not subscription_interval (string).
          // If we only send subscription_interval inside payload_json, $sellingPlanId stays empty,
          // the backend creates a one-off variant, and Shopify rejects the plan at cart-add time.
          const apiPayload = {
            payload_json: JSON.stringify(payload),
            formula_id: item.id,
          };
          if (selectedPlan) {
            payload.subscription_interval = selectedPlan;
            const resolvedPlanId = resolveSellingPlan(selectedPlan);
            if (resolvedPlanId) apiPayload.selling_plan = String(resolvedPlanId);
          }

          // Attach computed nutrition values as top-level fields so the PHP
          // backend uses them as $displayCalories/etc. directly. This bypasses
          // the slider-position-as-nutrition trap that the payload_json
          // flattening creates for saved formulas (including the Magento
          // migration). PHP's getNutritionLabel may still override these, but
          // if it fails the cart still shows real numbers instead of 0-100
          // positions.
          if (nut && !isPF) {
            const isProteinFormula = isProtein;
            if (!isProteinFormula) {
              if (nut.calories != null) apiPayload.calories = String(nut.calories);
              if (nut.carbs != null) apiPayload.carbohydrates = String(nut.carbs);
              if (nut.sodium != null) apiPayload.electrolytes = String(nut.sodium);
              if (nut.protein != null) apiPayload.protein = String(nut.protein);
              if (nut.bcaas != null) apiPayload.bcaas = String(nut.bcaas);
              if (nut.caffeine != null) apiPayload.caffeine = String(nut.caffeine);
            } else {
              // Protein cart properties are driven by Calories / Protein / Carbs /
              // BCAAs computed by the protein calculator. Forward them so the cart
              // shows real values for Magento-imported protein formulas too.
              if (nut.calories != null) apiPayload.calories = String(nut.calories);
              if (nut.protein != null) apiPayload.protein_g = String(nut.protein);
              if (nut.carbs != null) apiPayload.carbs = String(nut.carbs);
              if (nut.bcaas != null) apiPayload.bcaas = String(nut.bcaas);
            }
          }

          // Send payload_json for re-order flow
          const result = await api("POST", "add-to-cart", apiPayload);

          // Add to Shopify cart via AJAX
          if (result.variant_id) {
            const cartPayload = {
              items: [{
                id: parseInt(result.variant_id, 10),
                quantity: result.quantity || 1,
                properties: result.properties || {},
              }],
            };

            // Resolve selling plan: prefer backend response, fall back to client-side lookup
            const sellingPlanId = result.selling_plan || resolveSellingPlan(selectedPlan);
            if (sellingPlanId) {
              cartPayload.items[0].selling_plan = parseInt(sellingPlanId, 10);
            }

            // Retry cart add — newly created subscription variants may take a moment
            // to propagate through Shopify's inventory system.
            let cartResp;
            for (let attempt = 0; attempt < 5; attempt++) {
              if (attempt > 0) await new Promise(r => setTimeout(r, 3000));
              cartResp = await fetch("/cart/add.js", {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify(cartPayload),
              });
              if (cartResp.ok) break;
            }

            if (!cartResp.ok) {
              const cartErr = await cartResp.json().catch(() => ({}));
              throw new Error(cartErr.description || cartErr.message || "Failed to add to cart");
            }

            overlay.remove();

            // Brief delay before refreshing drawer — newly created subscription
            // variants need a moment to propagate through Shopify's section rendering.
            await new Promise(r => setTimeout(r, 1000));

            // Refresh and open the cart drawer so the new item is visible
            document.dispatchEvent(new CustomEvent("dispatch:cart-drawer:refresh", { bubbles: true }));
            document.dispatchEvent(new CustomEvent("dispatch:cart-drawer:open", { bubbles: true }));

            // Update cart count in header if possible
            try {
              const cartCount = document.querySelector(".cart-count, .js-cart-count, [data-cart-count]");
              if (cartCount) {
                const countResp = await fetch("/cart.js");
                const cartData = await countResp.json();
                cartCount.textContent = cartData.item_count;
              }
            } catch (e) { /* ignore */ }
          }
        } catch (e) {
          modalErrorEl.textContent = "Order failed: " + e.message;
          modalErrorEl.style.display = "block";
          orderBtn.disabled = false;
          orderBtn.textContent = "Add to Cart";
        }
      },
    }, ["Add to Cart"]);

    // Inline error element shown inside the modal near the order button
    const modalErrorEl = el("p", {
      class: "sf-order__error",
      style: "display:none;color:#c62828;font-size:13px;margin:8px 0 0;",
    });

    // Price display element
    priceEl = el("p", { class: "sf-order__price" });

    // Product image element
    imgEl = el("img", {
      src: getVariantImage(selectedPkg),
      alt: item.formula_name || "Custom Formula",
      class: "sf-order__image",
      onerror: function() { this.style.display = "none"; },
    });

    // Set initial price
    updatePriceDisplay();

    const modal = el("div", { class: "sf-modal sf-modal--order" }, [
      el("div", { class: "sf-modal__header" }, [
        el("h3", { class: "sf-modal__title" }, ["Order: " + (item.formula_name || "Custom Formula")]),
        el("button", { class: "sf-modal__close", onclick: () => overlay.remove() }, ["\u00D7"]),
      ]),
      el("div", { class: "sf-modal__body sf-order__body" }, [
        el("div", { class: "sf-order__image-col" }, [imgEl]),
        el("div", { class: "sf-order__details-col" }, [
          el("p", { class: "sf-order__formula-name" }, [item.formula_name || "Custom Formula"]),
          el("p", { class: "sf-order__formula-type" }, [ft + " Formula"]),
          el("p", { class: "sf-order__flavor" }, ["Flavor: " + (p.flavor || "N/A")]),
          nutritionToggle,
          pkgGroup,
          subGroup,
          priceEl,
          orderBtn,
          modalErrorEl,
        ]),
      ]),
    ]);

    overlay.appendChild(modal);
    document.body.appendChild(overlay);
  }

  // ─── Messages ───

  function showMsg(text, type) {
    const msgEl = document.getElementById("sf-msg");
    if (!msgEl) return;
    msgEl.textContent = text;
    msgEl.className = "sf-msg " + (type === "success" ? "sf-msg--success" : "sf-msg--error");
    if (type === "success") {
      setTimeout(() => {
        if (msgEl.textContent === text) {
          msgEl.textContent = "";
          msgEl.className = "sf-msg";
        }
      }, 4000);
    }
  }

  // ─── Rendering ───

  function renderShell() {
    root.innerHTML = "";

    const searchInput = el("input", {
      type: "search",
      placeholder: "Search by formula name, flavor, package size...",
      value: state.query,
      class: "sf-search",
      oninput: (e) => {
        state.query = e.target.value;
        state.page = 1;
        debounceReload();
      },
    });

    const perPageInput = el("input", {
      type: "number",
      min: "1",
      max: "50",
      value: String(state.pageSize),
      class: "sf-perpage",
      onchange: (e) => {
        state.pageSize = Math.max(1, Math.min(50, parseInt(e.target.value, 10) || 10));
        state.page = 1;
        reload();
      },
    });

    const refreshBtn = el("button", {
      class: "sf-btn sf-btn--secondary",
      onclick: () => reload(),
    }, ["Refresh"]);

    const controls = el("div", { class: "sf-controls" }, [
      el("div", { class: "sf-controls__left" }, [
        el("label", { class: "sf-label" }, ["Search"]),
        searchInput,
      ]),
      el("div", { class: "sf-controls__right" }, [
        el("label", { class: "sf-label" }, ["Per page"]),
        perPageInput,
        refreshBtn,
      ]),
    ]);

    const msg = el("div", { id: "sf-msg", class: "sf-msg" });
    const tableWrap = el("div", { class: "sf-table-wrap" });
    const table = el("table", { class: "sf-table" });
    const thead = el("thead");
    const headRow = el("tr");

    const cols = [
      ["formula_name", "Formula Name"],
      [null, "Formula Type"],
      [null, "Flavor"],
      [null, "Boosts"],
      [null, "Nutrition Info"],
      ["updated_at", "Updated"],
    ];

    cols.forEach(([key, label]) => {
      const attrs = {};
      if (key) {
        attrs.role = "button";
        attrs.tabindex = "0";
        attrs["data-sort"] = key;
        attrs.class = "sf-th--sortable" + (state.sortBy === key ? " sf-th--active" : "");
        attrs.onclick = () => toggleSort(key);
      }
      headRow.appendChild(el("th", attrs, [
        label + (key && state.sortBy === key ? (state.sortDir === "asc" ? " \u25B2" : " \u25BC") : ""),
      ]));
    });

    headRow.appendChild(el("th", { class: "sf-th--actions" }, ["Actions"]));
    thead.appendChild(headRow);
    table.appendChild(thead);
    table.appendChild(el("tbody", { id: "sf-body" }));
    tableWrap.appendChild(table);

    const pager = el("div", { class: "sf-pager" }, [
      el("button", { id: "sf-prev", class: "sf-btn sf-btn--secondary", onclick: () => { if (state.page > 1) { state.page--; reload(); } } }, ["Prev"]),
      el("span", { id: "sf-meta", class: "sf-pager__meta" }),
      el("button", { id: "sf-next", class: "sf-btn sf-btn--secondary", onclick: () => { state.page++; reload(); } }, ["Next"]),
    ]);

    root.appendChild(controls);
    root.appendChild(msg);
    root.appendChild(tableWrap);
    root.appendChild(pager);
    root.appendChild(buildStyles());
  }

  function renderRows(items) {
    const body = document.getElementById("sf-body");
    body.innerHTML = "";

    if (!items || items.length === 0) {
      body.innerHTML = '<tr><td colspan="7" class="sf-empty">No saved formulas found.</td></tr>';
      return;
    }

    items.forEach((item) => {
      const ft = getFormulaType(item);
      const p = item.payload || {};

      // Boosts display — strip trailing "?" from labels (legacy data included help button text)
      const boostLabels = (p.boosts && p.boosts.labels && p.boosts.labels.length > 0)
        ? p.boosts.labels.map(function (l) { return String(l).replace(/\s*\?\s*$/, '').trim(); })
        : [];
      const boostText = boostLabels.length > 0 ? boostLabels.join(", ") : "\u2014";

      // Action buttons
      const orderBtn = el("button", {
        class: "sf-action sf-action--order",
        title: "Order this formula",
        onclick: () => handleOrder(item),
      }, ["Order"]);

      const copyBtn = el("button", {
        class: "sf-action sf-action--copy",
        title: "Copy this formula",
        onclick: () => handleCopy(item),
      }, ["Copy"]);

      const editBtn = el("button", {
        class: "sf-action sf-action--edit",
        title: "Edit this formula",
        onclick: () => handleEdit(item),
      }, ["Edit"]);

      const deleteBtn = el("button", {
        class: "sf-action sf-action--delete",
        title: "Delete this formula",
        onclick: () => handleDelete(item),
      }, ["Delete"]);

      // Nutrition info button
      const nutritionBtn = el("button", {
        class: "sf-nutrition-btn",
        onclick: () => showNutritionModal(item),
      }, ["View"]);

      const tr = el("tr", {}, [
        el("td", { class: "sf-td--name" }, [item.formula_name || ""]),
        el("td", {}, [
          el("span", { class: "sf-type-badge " + getFormulaTypeClass(ft) }, [ft]),
        ]),
        el("td", {}, [p.flavor || item.flavor || ""]),
        el("td", { class: "sf-td--boosts" }, [boostText]),
        el("td", { class: "sf-td--nutrition" }, [nutritionBtn]),
        el("td", { class: "sf-td--date" }, [fmtDate(item.updated_at)]),
        el("td", { class: "sf-td--actions" }, [orderBtn, copyBtn, editBtn, deleteBtn]),
      ]);

      body.appendChild(tr);
    });
  }

  function toggleSort(key) {
    if (state.sortBy === key) state.sortDir = state.sortDir === "asc" ? "desc" : "asc";
    else { state.sortBy = key; state.sortDir = "asc"; }
    state.page = 1;
    reload();
  }

  let t = null;
  function debounceReload() {
    if (t) clearTimeout(t);
    t = setTimeout(() => reload(), 250);
  }

  async function reload() {
    const msg = document.getElementById("sf-msg");
    if (msg) { msg.textContent = ""; msg.className = "sf-msg"; }

    const body = document.getElementById("sf-body");
    if (body) body.innerHTML = '<tr><td colspan="7" class="sf-loading">Loading\u2026</td></tr>';

    try {
      const data = await fetchList();
      state.items = data.items || [];
      state.total = data.total || 0;
      state.page = data.page || 1;

      renderRows(state.items);

      const meta = document.getElementById("sf-meta");
      const maxPage = Math.max(1, Math.ceil(state.total / state.pageSize));
      if (meta) meta.textContent = `Page ${state.page} of ${maxPage} \u2014 ${state.total} total`;

      const prevBtn = document.getElementById("sf-prev");
      const nextBtn = document.getElementById("sf-next");
      if (prevBtn) prevBtn.disabled = state.page <= 1;
      if (nextBtn) nextBtn.disabled = state.page >= maxPage;
    } catch (e) {
      if (body) body.innerHTML = '<tr><td colspan="7" class="sf-empty">\u2014</td></tr>';
      showMsg(e.message || "Failed to load.", "error");
    }
  }

  // ─── Styles ───

  function buildStyles() {
    return el("style", {}, [`
      /* Container padding */
      #saved-formulas-app { padding: 0 0 80px; width: 100%; }

      /* Controls */
      .sf-controls { display: flex; justify-content: space-between; align-items: flex-end; gap: 1rem; margin-bottom: 1.5rem; flex-wrap: wrap; }
      .sf-controls__left { flex: 1; min-width: 200px; }
      .sf-controls__right { display: flex; align-items: flex-end; gap: 0.5rem; }
      .sf-label { display: block; font-size: 13px; font-weight: 600; margin-bottom: 4px; text-transform: uppercase; letter-spacing: 0.05em; }
      .sf-search { width: 100%; padding: 10px 14px; font-size: 15px; border: 1px solid #ccc; border-radius: 4px; }
      .sf-search:focus { border-color: #000; outline: none; }
      .sf-perpage { width: 70px; padding: 10px 8px; font-size: 15px; border: 1px solid #ccc; border-radius: 4px; text-align: center; }

      /* Table */
      .sf-table-wrap { overflow-x: auto; }
      .sf-table { width: 100%; border-collapse: collapse; font-size: 14px; }
      .sf-table th { text-align: left; padding: 10px 8px; border-bottom: 2px solid #222; font-weight: 700; text-transform: uppercase; font-size: 12px; letter-spacing: 0.05em; white-space: nowrap; }
      .sf-table td { padding: 10px 8px; border-bottom: 1px solid #eee; vertical-align: middle; }
      .sf-table tbody tr:hover { background: #f9f9f9; }
      .sf-th--sortable { cursor: pointer; user-select: none; }
      .sf-th--sortable:hover { color: #666; }
      .sf-th--active { color: #000; }
      .sf-th--actions { text-align: right; width: 1%; white-space: nowrap; }
      .sf-table th:nth-child(6), .sf-td--date { width: 1%; white-space: nowrap; }

      .sf-td--name { font-weight: 600; }
      .sf-td--date { font-size: 13px; color: #666; white-space: nowrap; }
      .sf-td--actions { text-align: right; white-space: nowrap; }
      .sf-td--nutrition { text-align: center; }
      .sf-table th:nth-child(5), .sf-td--nutrition { width: 1%; white-space: nowrap; }
      .sf-td--boosts { font-size: 13px; color: #444; }
      .sf-table th:nth-child(4), .sf-td--boosts { width: auto; }

      .sf-empty, .sf-loading { text-align: center; color: #999; padding: 2rem !important; }

      /* Type badges */
      .sf-type-badge { display: inline-block; padding: 3px 10px; border-radius: 3px; font-size: 12px; font-weight: 700; text-transform: uppercase; letter-spacing: 0.03em; }
      .sf-type-badge--fuel { background: #fbb117; color: #fff; }
      .sf-type-badge--protein { background: #c41230; color: #fff; }

      /* Nutrition button */
      .sf-nutrition-btn { background: #f2d500; border: none; border-radius: 4px; padding: 4px 14px; font-size: 12px; font-weight: 700; cursor: pointer; color: #222; }
      .sf-nutrition-btn:hover { background: #ffe066; }

      /* Action buttons */
      .sf-action { border: none; background: none; cursor: pointer; font-size: 13px; font-weight: 600; padding: 4px 8px; border-radius: 3px; margin-left: 2px; text-decoration: none; }
      .sf-action--order { color: #2e7d32; }
      .sf-action--order:hover { background: #e8f5e9; }
      .sf-action--copy { color: #1565c0; }
      .sf-action--copy:hover { background: #e3f2fd; }
      .sf-action--edit { color: #e65100; }
      .sf-action--edit:hover { background: #fff3e0; }
      .sf-action--delete { color: #c62828; }
      .sf-action--delete:hover { background: #ffebee; }

      /* Buttons */
      .sf-btn { border: none; border-radius: 4px; padding: 8px 18px; font-size: 14px; font-weight: 600; cursor: pointer; transition: background 0.15s; }
      .sf-btn--primary { background: #222; color: #fff; }
      .sf-btn--primary:hover { background: #444; }
      .sf-btn--primary:disabled { background: #999; cursor: not-allowed; }
      .sf-btn--secondary { background: #f0f0f0; color: #222; border: 1px solid #ccc; }
      .sf-btn--secondary:hover { background: #e0e0e0; }
      .sf-btn--secondary:disabled { opacity: 0.5; cursor: not-allowed; }
      .sf-btn--lg { padding: 12px 28px; font-size: 16px; width: 100%; margin-top: 1rem; }

      /* Pager */
      .sf-pager { display: flex; gap: 0.75rem; align-items: center; margin-top: 1rem; justify-content: center; }
      .sf-pager__meta { font-size: 14px; color: #666; }

      /* Messages */
      .sf-msg { margin: 0.5rem 0; padding: 0; font-size: 14px; min-height: 1.5em; }
      .sf-msg--error { color: #c62828; background: #ffebee; padding: 10px 14px; border-radius: 4px; }
      .sf-msg--success { color: #2e7d32; background: #e8f5e9; padding: 10px 14px; border-radius: 4px; }

      /* Modal */
      .sf-modal-overlay { position: fixed; top: 0; left: 0; right: 0; bottom: 0; background: rgba(0,0,0,0.5); z-index: 9999; display: flex; align-items: center; justify-content: center; padding: 1rem; }
      .sf-modal { background: #fff; border-radius: 8px; max-width: 700px; width: 100%; max-height: 90vh; overflow-y: auto; overflow-x: hidden; box-shadow: 0 8px 32px rgba(0,0,0,0.2); }
      .sf-modal--nutrition.sf-modal { overflow: hidden; max-height: none; }
      .sf-modal--order { max-width: 800px; }
      .sf-modal--nutrition { max-width: 420px; width: auto; overflow: hidden; }
      .sf-modal--nutrition .sf-modal__body { overflow: hidden; }
      .sf-nutrition-label-body { display: flex; justify-content: center; padding: 4px 8px; overflow: hidden; }
      .sf-nutrition-label-body .nutrition-label { transform-origin: top center; }
      .sf-nutrition-label-body table { font-size: 11px; }
      .sf-nutrition-label-body .nf { margin: 0 auto; }
      .sf-modal__header { display: flex; justify-content: space-between; align-items: center; padding: 10px 14px; border-bottom: 1px solid #eee; }
      .sf-modal__title { margin: 0; font-size: 16px; font-weight: 700; }
      .sf-modal__close { background: none; border: none; font-size: 24px; cursor: pointer; color: #999; padding: 0 4px; line-height: 1; }
      .sf-modal__close:hover { color: #333; }
      .sf-modal__body { padding: 10px 14px; }
      .sf-modal--nutrition .sf-modal__body { padding: 4px 8px; }
      .sf-modal__footer { padding: 8px 14px; border-top: 1px solid #eee; text-align: right; }

      /* Order modal */
      .sf-order__body { display: flex; gap: 24px; }
      .sf-order__image-col { flex: 0 0 200px; }
      .sf-order__image { width: 100%; height: auto; border-radius: 8px; }
      .sf-order__details-col { flex: 1; }
      .sf-order__formula-name { font-size: 20px; font-weight: 700; margin: 0 0 4px; }
      .sf-order__formula-type { font-size: 14px; color: #666; margin: 0 0 4px; }
      .sf-order__flavor { font-size: 14px; margin: 0 0 16px; }
      .sf-order__section { margin-bottom: 16px; }
      .sf-order__section-title { font-size: 14px; font-weight: 700; text-transform: uppercase; margin: 0 0 8px; letter-spacing: 0.05em; }
      .sf-order__radio-label { display: block; padding: 6px 0; font-size: 14px; cursor: pointer; }
      .sf-order__radio-label input { margin-right: 8px; }
      .sf-order__nutrition-toggle { background: none; border: none; color: #1565c0; cursor: pointer; font-size: 14px; font-weight: 600; padding: 8px 0; text-align: left; }
      .sf-order__nutrition-toggle:hover { text-decoration: underline; }
      .sf-order__nutrition-content { padding: 12px 0; }
      .sf-order__price { font-size: 22px; font-weight: 700; margin: 12px 0 0; color: #222; }
      .sf-order__price-note { font-size: 13px; font-weight: 400; color: #666; }

      @media (max-width: 640px) {
        .sf-order__body { flex-direction: column; }
        .sf-order__image-col { flex: none; text-align: center; }
        .sf-order__image { max-width: 200px; }
        .sf-controls { flex-direction: column; }
        .sf-controls__right { width: 100%; justify-content: flex-end; }
        .sf-table { font-size: 13px; }
        .sf-action { font-size: 12px; padding: 3px 6px; }
      }
    `]);
  }

  // ─── Init ───

  renderShell();
  reload();
})();
